import type {
  AnalyticsLiveSummary,
  AnalyticsUpcomingGap,
} from './analyticsService';
import { leaveService, type LeaveRequest } from './leaveService';
import {
  reportService,
  type ReportListItem,
  type ReportStatusCounts,
} from './reportService';
import { supabase } from '../supabaseClient';
import { resolveSiteTimeZone, shiftDateKey } from '../utils/siteScheduling';

export type CommandDataSection = 'operations' | 'leave' | 'reports' | 'licences';

export interface CommandLicenceWarning {
  id: string;
  guardId: string;
  guardName: string;
  licenceTypeName: string;
  expiryDate: string;
  daysUntilExpiry: number;
}

export interface CommandReportFeed {
  reports: ReportListItem[];
  totalCount: number;
  statusCounts: ReportStatusCounts;
}

export interface CommandNoShow {
  shiftId: string;
  siteId: string;
  siteName: string;
  guardId: string;
  guardName: string;
  slotDate: string;
  startMinute: number;
  endMinute: number;
  dutyId?: string | null;
}

export interface CommandOperations {
  noShows: CommandNoShow[];
  upcomingGaps: AnalyticsUpcomingGap[];
  live: AnalyticsLiveSummary;
  noShowCount?: number;
  coverageIssueCount?: number;
  openPositionCount?: number;
  liveAvailable?: boolean;
}

export interface CommandSnapshot {
  operations: CommandOperations | null;
  pendingLeaveRequests: LeaveRequest[];
  incidentReports: CommandReportFeed;
  summaryReports: CommandReportFeed;
  licenceWarnings: CommandLicenceWarning[];
  licenceWarningCount?: number;
  unavailableSections: CommandDataSection[];
  generatedAt: string;
}

interface LicenceWarningRow {
  id: string;
  guard_id: string;
  licence_type_id: string | null;
  expiry_date: string;
  guard?: { full_name: string | null } | Array<{ full_name: string | null }> | null;
  licence_type?: { name: string | null } | Array<{ name: string | null }> | null;
}

interface CommandSiteRow {
  id?: string;
  name: string | null;
  country_code: string | null;
  timezone: string | null;
}

interface CommandDutySlotRow {
  id: string;
  site_id: string | null;
  slot_date: string;
  name: string | null;
  duty_kind: string;
  start_minute: number;
  end_minute: number;
  guards_required: number;
  is_published: boolean;
  required_licence_type_id: string | null;
  assignments?: Array<{ id: string }> | null;
}

interface CommandShiftRow {
  id: string;
  shift_key: string;
  site_id: string;
  guard_id: string;
  slot_date: string;
  start_minute: number;
  end_minute: number;
  is_published: boolean;
  guard?: { full_name: string | null } | Array<{ full_name: string | null }> | null;
  site?: CommandSiteRow | CommandSiteRow[] | null;
  segments?: Array<{ source_slot_id: string | null; segment_type: string }> | null;
}

interface CommandExecutionRow {
  id: string;
  shift_id: string | null;
  site_id: string | null;
  planned_slot_date: string;
  planned_shift_key: string | null;
  planned_end_at: string | null;
  actual_clock_in_at: string | null;
  actual_clock_out_at: string | null;
  status: string | null;
}

interface CommandLiveLocationRow {
  guard_id: string;
  updated_at: string;
  battery: number | null;
}

const PAGE_SIZE = 1000;

const EMPTY_STATUS_COUNTS: ReportStatusCounts = {
  open: 0,
  submitted: 0,
  in_review: 0,
  needs_more_info: 0,
  escalated: 0,
  closed: 0,
};

const EMPTY_REPORT_FEED: CommandReportFeed = {
  reports: [],
  totalCount: 0,
  statusCounts: EMPTY_STATUS_COUNTS,
};

function unwrapRelation<T>(value: T | T[] | null | undefined): T | null {
  return Array.isArray(value) ? value[0] ?? null : value ?? null;
}

function toDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function daysBetweenDateKeys(left: string, right: string): number {
  const leftDate = new Date(`${left}T00:00:00Z`).getTime();
  const rightDate = new Date(`${right}T00:00:00Z`).getTime();
  return Math.round((leftDate - rightDate) / 86_400_000);
}

function dateKeyToSerial(value: string): number {
  const [year, month, day] = value.split('-').map((part) => Number.parseInt(part, 10));
  return Math.floor(Date.UTC(year, month - 1, day) / 86_400_000);
}

function getZonedNowState(now: Date, timeZone: string): { dateKey: string; minuteOfDay: number } {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const parts = Object.fromEntries(
    formatter.formatToParts(now).filter((part) => part.type !== 'literal').map((part) => [part.type, part.value]),
  );
  const hours = Number(parts.hour ?? '0') % 24;
  return {
    dateKey: `${parts.year}-${parts.month}-${parts.day}`,
    minuteOfDay: (hours * 60) + Number(parts.minute ?? '0'),
  };
}

function isPastNoShowGrace(shift: CommandShiftRow, now: Date): boolean {
  const site = unwrapRelation(shift.site);
  const current = getZonedNowState(now, resolveSiteTimeZone(site));
  const dayDelta = dateKeyToSerial(current.dateKey) - dateKeyToSerial(shift.slot_date);
  const minutesSinceStart = (dayDelta * 1440) + current.minuteOfDay - shift.start_minute;
  return minutesSinceStart >= 15;
}

async function fetchAllRows<T>(
  queryFactory: (from: number, to: number) => Promise<{ data: T[] | null; error: unknown }>,
): Promise<T[]> {
  const rows: T[] = [];
  for (let from = 0; ; from += PAGE_SIZE) {
    const { data, error } = await queryFactory(from, from + PAGE_SIZE - 1);
    if (error) throw error;
    const batch = data ?? [];
    rows.push(...batch);
    if (batch.length < PAGE_SIZE) return rows;
  }
}

async function fetchCommandOperations(companyId: string): Promise<CommandOperations> {
  const now = new Date();
  const todayKey = toDateKey(now);
  const startDate = shiftDateKey(todayKey, -1);
  const futureEndDate = shiftDateKey(todayKey, 13);
  const noShowEndDate = shiftDateKey(todayKey, 1);
  const liveTrackStart = new Date(now.getTime() - 15 * 60_000).toISOString();

  const [dutySlots, sites, shifts, executions, activeExecutionRows, liveResult] = await Promise.all([
    fetchAllRows<CommandDutySlotRow>((from, to) => supabase
      .from('duty_slots')
      .select(`
        id,
        site_id,
        slot_date,
        name,
        duty_kind,
        start_minute,
        end_minute,
        guards_required,
        is_published,
        required_licence_type_id,
        assignments:duty_slot_assignments(id)
      `)
      .eq('company_id', companyId)
      // Include the previous source day because a duty may use minutes above
      // 1440 and therefore begin after midnight on the current calendar day.
      .gte('slot_date', startDate)
      .lte('slot_date', futureEndDate)
      .order('slot_date', { ascending: true })
      .order('start_minute', { ascending: true })
      .order('id', { ascending: true })
      .range(from, to) as unknown as Promise<{ data: CommandDutySlotRow[] | null; error: unknown }>),
    fetchAllRows<CommandSiteRow & { id: string }>((from, to) => supabase
      .from('sites')
      .select('id, name, country_code, timezone')
      .eq('company_id', companyId)
      .order('id', { ascending: true })
      .range(from, to) as unknown as Promise<{ data: Array<CommandSiteRow & { id: string }> | null; error: unknown }>),
    fetchAllRows<CommandShiftRow>((from, to) => supabase
      .from('shifts')
      .select(`
        id,
        shift_key,
        site_id,
        guard_id,
        slot_date,
        start_minute,
        end_minute,
        is_published,
        guard:profiles(full_name),
        site:sites(name, country_code, timezone),
        segments:shift_segments(source_slot_id, segment_type)
      `)
      .eq('company_id', companyId)
      .eq('is_published', true)
      .gte('slot_date', startDate)
      .lte('slot_date', noShowEndDate)
      .order('slot_date', { ascending: false })
      .order('start_minute', { ascending: false })
      .order('id', { ascending: true })
      .range(from, to) as unknown as Promise<{ data: CommandShiftRow[] | null; error: unknown }>),
    fetchAllRows<CommandExecutionRow>((from, to) => supabase
      .from('shift_executions')
      .select('id, shift_id, site_id, planned_shift_key, planned_slot_date, planned_end_at, actual_clock_in_at, actual_clock_out_at, status')
      .eq('company_id', companyId)
      .gte('planned_slot_date', startDate)
      .lte('planned_slot_date', noShowEndDate)
      .order('planned_slot_date', { ascending: true })
      .order('id', { ascending: true })
      .range(from, to) as unknown as Promise<{ data: CommandExecutionRow[] | null; error: unknown }>),
    // Keep live execution counts independent of the no-show window so a
    // serious multi-day overrun never disappears from Operational pulse.
    fetchAllRows<CommandExecutionRow>((from, to) => supabase
      .from('shift_executions')
      .select('id, shift_id, site_id, planned_shift_key, planned_slot_date, planned_end_at, actual_clock_in_at, actual_clock_out_at, status')
      .eq('company_id', companyId)
      .eq('status', 'active')
      .is('actual_clock_out_at', null)
      .order('id', { ascending: true })
      .range(from, to) as unknown as Promise<{ data: CommandExecutionRow[] | null; error: unknown }>),
    fetchAllRows<CommandLiveLocationRow>((from, to) => supabase
      .from('guard_live_locations')
      .select('guard_id, updated_at, battery')
      .gte('updated_at', liveTrackStart)
      .order('updated_at', { ascending: false })
      .order('guard_id', { ascending: true })
      .range(from, to) as unknown as Promise<{ data: CommandLiveLocationRow[] | null; error: unknown }>)
      .then((rows) => ({ rows, available: true }))
      .catch(() => ({ rows: [] as CommandLiveLocationRow[], available: false })),
  ]);

  const siteMap = new Map(sites.map((site) => [site.id, site]));
  const executionShiftIds = new Set(executions.map((row) => row.shift_id).filter(Boolean));
  const executionShiftKeys = new Set(executions.map((row) => row.planned_shift_key).filter(Boolean));

  const horizonDutySlots = dutySlots.filter((slot) => {
    const occurrenceDate = shiftDateKey(slot.slot_date, Math.floor(slot.start_minute / 1440));
    const normalizedEndMinute = slot.end_minute <= slot.start_minute
      ? slot.end_minute + 1440
      : slot.end_minute;
    const occurrenceEndDate = shiftDateKey(
      slot.slot_date,
      Math.floor(Math.max(normalizedEndMinute - 1, slot.start_minute) / 1440),
    );
    return occurrenceEndDate >= todayKey && occurrenceDate <= futureEndDate;
  });

  const allUpcomingGaps = horizonDutySlots
    .map((slot) => {
      const assignedCount = Math.min(slot.assignments?.length ?? 0, slot.guards_required);
      const openPositions = Math.max(slot.guards_required - assignedCount, 0);
      const site = slot.site_id ? siteMap.get(slot.site_id) : null;
      return {
        dutyId: slot.id,
        siteId: slot.site_id,
        siteName: site?.name?.trim() || '',
        // The page localizes this duty-kind fallback when no custom name exists.
        dutyName: slot.name?.trim() || '',
        dutyKind: slot.duty_kind,
        slotDate: slot.slot_date,
        occurrenceDate: shiftDateKey(slot.slot_date, Math.floor(slot.start_minute / 1440)),
        startMinute: slot.start_minute,
        endMinute: slot.end_minute,
        guardsRequired: slot.guards_required,
        assignedCount,
        openPositions,
        isPublished: slot.is_published,
        requiredLicenceTypeId: slot.required_licence_type_id,
      } satisfies AnalyticsUpcomingGap;
    })
    .filter((slot) => slot.openPositions > 0 || !slot.isPublished);

  // Command flags a no-show after a 15-minute site-local grace period. A
  // matching execution by durable id or generated shift key clears the item.
  const allNoShows = shifts
    .filter((shift) => (
      isPastNoShowGrace(shift, now)
      && !executionShiftIds.has(shift.id)
      && !executionShiftKeys.has(shift.shift_key)
    ))
    .map((shift) => ({
      shiftId: shift.id,
      siteId: shift.site_id,
      siteName: unwrapRelation(shift.site)?.name?.trim() || '',
      guardId: shift.guard_id,
      guardName: unwrapRelation(shift.guard)?.full_name?.trim() || '',
      slotDate: shift.slot_date,
      startMinute: shift.start_minute,
      endMinute: shift.end_minute,
      dutyId: shift.segments?.find((segment) => segment.segment_type === 'duty')?.source_slot_id ?? null,
    } satisfies CommandNoShow));

  const activeExecutions = activeExecutionRows.filter((row) => (
    Boolean(row.actual_clock_in_at) && !row.actual_clock_out_at
  ));
  const trackedGuards = liveResult.rows;

  return {
    noShows: allNoShows.slice(0, 12),
    upcomingGaps: allUpcomingGaps.slice(0, 12),
    noShowCount: allNoShows.length,
    coverageIssueCount: allUpcomingGaps.length,
    openPositionCount: allUpcomingGaps.reduce((total, gap) => total + gap.openPositions, 0),
    liveAvailable: liveResult.available,
    live: {
      activeExecutions: activeExecutions.length,
      activeSites: new Set(activeExecutions.map((row) => row.site_id).filter(Boolean)).size,
      overrunningExecutions: activeExecutions.filter((row) => (
        Boolean(row.planned_end_at) && new Date(row.planned_end_at!).getTime() < now.getTime()
      )).length,
      liveTrackedGuards: trackedGuards.length,
      lowBatteryCount: trackedGuards.filter((guard) => guard.battery != null && guard.battery < 20).length,
      activeSiteDepartures: 0,
      manualInterventions: 0,
    },
  };
}

async function fetchLicenceWarnings(companyId: string): Promise<{ warnings: CommandLicenceWarning[]; totalCount: number }> {
  const today = new Date();
  const horizon = new Date(today);
  horizon.setDate(horizon.getDate() + 30);
  const todayKey = toDateKey(today);

  const rows = await fetchAllRows<LicenceWarningRow>((from, to) => supabase
    .from('guard_licences')
    .select(`
      id,
      guard_id,
      licence_type_id,
      expiry_date,
      guard:profiles!guard_licences_guard_id_fkey(full_name),
      licence_type:licence_types!guard_licences_licence_type_id_fkey(name)
    `)
    .eq('company_id', companyId)
    .in('status', ['approved', 'expired'])
    .order('expiry_date', { ascending: false })
    .order('id', { ascending: true })
    .range(from, to) as unknown as Promise<{ data: LicenceWarningRow[] | null; error: unknown }>);

  // Only the latest accepted credential per guard/type is operationally
  // relevant. This prevents an old expired document warning after renewal.
  const latestByGuardType = new Map<string, LicenceWarningRow>();
  rows.forEach((row) => {
    const key = `${row.guard_id}:${row.licence_type_id ?? row.id}`;
    if (!latestByGuardType.has(key)) latestByGuardType.set(key, row);
  });

  const warnings = [...latestByGuardType.values()]
    .filter((row) => row.expiry_date <= toDateKey(horizon))
    .map((row) => {
      const guard = unwrapRelation(row.guard);
      const licenceType = unwrapRelation(row.licence_type);
      return {
        id: row.id,
        guardId: row.guard_id,
        guardName: guard?.full_name?.trim() || '',
        licenceTypeName: licenceType?.name?.trim() || '',
        expiryDate: row.expiry_date,
        daysUntilExpiry: daysBetweenDateKeys(row.expiry_date, todayKey),
      } satisfies CommandLicenceWarning;
    })
    .sort((left, right) => {
      const leftExpired = left.daysUntilExpiry < 0;
      const rightExpired = right.daysUntilExpiry < 0;
      if (leftExpired !== rightExpired) return leftExpired ? -1 : 1;
      if (leftExpired) return right.daysUntilExpiry - left.daysUntilExpiry;
      return left.daysUntilExpiry - right.daysUntilExpiry;
    });

  return { warnings: warnings.slice(0, 20), totalCount: warnings.length };
}

function reportFeedFromResult(
  result: Awaited<ReturnType<typeof reportService.fetchReports>>,
): CommandReportFeed {
  return {
    reports: result.reports,
    totalCount: result.totalCount,
    statusCounts: result.statusCounts,
  };
}

export const commandService = {
  /**
   * Load independent command-centre feeds without allowing one unavailable
   * backend feature to blank the operational page. Live alarms and user
   * notifications are supplied by DashboardShell's realtime hooks instead.
   */
  async fetchSnapshot(_cacheBust = 0): Promise<CommandSnapshot> {
    const profilePromise = reportService.getCurrentUserProfile();

    const [operationsResult, leaveResult, reportsResult, licencesResult] = await Promise.allSettled([
      profilePromise.then((profile) => fetchCommandOperations(profile.company_id)),
      leaveService.fetchLeaveRequests('pending'),
      profilePromise.then(async (profile) => {
        const [incident, summary] = await Promise.all([
          reportService.fetchReports({
            companyId: profile.company_id,
            page: 0,
            pageSize: 4,
            category: 'incident',
            sortBy: 'updated_desc',
          }),
          reportService.fetchReports({
            companyId: profile.company_id,
            page: 0,
            pageSize: 4,
            category: 'summary',
            sortBy: 'updated_desc',
          }),
        ]);
        return { incident, summary };
      }),
      profilePromise.then((profile) => fetchLicenceWarnings(profile.company_id)),
    ]);

    const unavailableSections: CommandDataSection[] = [];
    if (operationsResult.status === 'rejected') unavailableSections.push('operations');
    if (leaveResult.status === 'rejected') unavailableSections.push('leave');
    if (reportsResult.status === 'rejected') unavailableSections.push('reports');
    if (licencesResult.status === 'rejected') unavailableSections.push('licences');

    return {
      operations: operationsResult.status === 'fulfilled' ? operationsResult.value : null,
      pendingLeaveRequests: leaveResult.status === 'fulfilled' ? leaveResult.value : [],
      incidentReports: reportsResult.status === 'fulfilled'
        ? reportFeedFromResult(reportsResult.value.incident)
        : EMPTY_REPORT_FEED,
      summaryReports: reportsResult.status === 'fulfilled'
        ? reportFeedFromResult(reportsResult.value.summary)
        : EMPTY_REPORT_FEED,
      licenceWarnings: licencesResult.status === 'fulfilled' ? licencesResult.value.warnings : [],
      licenceWarningCount: licencesResult.status === 'fulfilled' ? licencesResult.value.totalCount : undefined,
      unavailableSections,
      generatedAt: new Date().toISOString(),
    };
  },
};
